import { bandOf, orderDay, type Band, type DayRow } from './day-order'
import { houseOrder, type JobKind } from './jobs'

// ---- the Diary's one order for a day (tidy-up, Liam, 20 Oct 2026: option A; Astra's review, 20 Oct 2026) ------------
//
// Stop by stop: with several jobs on, a house's visits stay together, gutters first (jobs.ts houseOrder), each taking
// the band of the house's first visit in the day's order. Stops with a time come first, in time order, agreed or not;
// then the ones whose day is agreed with no time; then the ones nobody has agreed; then the completed ones. The
// Diary's rows, their numbers, the day's map, both booking previews and the computer's headings all read this, so a
// visit pencilled in at 9am sits above an agreed 10am visit. Today keeps its own order (today.ts, orderDay).
export type DiaryGroup = 'timed' | 'agreed' | 'pencilled' | 'completed'
type StopRow = DayRow & { customer_id: number; kind?: JobKind | null }

export function diaryDayGroups<T extends StopRow>(visits: readonly T[], severalJobs: boolean): Record<DiaryGroup, T[]> {
  const booked = visits.filter((v) => v.state !== 'done')
  const done = visits.filter((v) => v.state === 'done')
  const houseBand = new Map<number, Band>()
  // The first of a house's visits in the day's own order (before its gutters are pulled to the front): a windows visit
  // at 10am puts the whole house among the times.
  if (severalJobs) for (const v of orderDay(booked)) if (!houseBand.has(v.customer_id)) houseBand.set(v.customer_id, bandOf(v))
  const band = (v: T): Band => houseBand.get(v.customer_id) ?? bandOf(v)
  const house = (rows: T[]): T[] => (severalJobs ? houseOrder(rows) : rows)
  return {
    timed: house(orderDay(booked.filter((v) => band(v) === 0))),
    agreed: house(orderDay(booked.filter((v) => band(v) === 1))),
    pencilled: house(orderDay(booked.filter((v) => band(v) === 2))),
    completed: house(orderDay(done)),
  }
}

export function diaryDayOrder<T extends StopRow>(visits: readonly T[], severalJobs: boolean): T[] {
  const g = diaryDayGroups(visits, severalJobs)
  return [...g.timed, ...g.agreed, ...g.pencilled, ...g.completed]
}

// ---- the day the Diary opens on (Liam, 20 Oct 2026: "i wonder if it should open here in day view of diary only to the
// next day with any work on thats what i find myself doing constantly ... we can save a touch here";
// https://claude.ai/artifact/2DmFBTyzdHV7oXmnHxnW5s) ------------------------------------------------------------------
//
// Today while it has a visit still to do (`workLeft`); otherwise the first day after it with visits by the day strip's
// own count (`hasWork`), up to 14 days ahead; else today. `known` is the last day read, or saved on the phone with no
// signal: null when the answer may lie beyond it (read further, then ask again).
export const OPEN_AHEAD_DAYS = 14
const dayAfter = (iso: string, n = 1): string => {
  const d = new Date(`${iso}T12:00:00Z`)
  d.setUTCDate(d.getUTCDate() + n)
  return d.toISOString().slice(0, 10)
}
export function openingDay(today: string, known: string, workLeft: (iso: string) => boolean, hasWork: (iso: string) => boolean): string | null {
  if (workLeft(today)) return today
  const last = dayAfter(today, OPEN_AHEAD_DAYS)
  for (let d = dayAfter(today); d <= last; d = dayAfter(d)) {
    if (d > known) return null
    if (hasWork(d)) return d
  }
  return today
}
